import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { taskStatusesConfig, FullProject } from 'entities';

import { UserAvatarGroup } from 'components/UserAvatar';
import { Button, Dot, Link, Row, Column, Tag } from 'components/Blocks';
import { Filters } from 'components/ListManipulation';

import { useFormControl } from 'hooks';
import { useContainerScroll } from 'hooks/appWrapperScroll';

import { formatDate } from 'utils/date';

import { ProjectFilter } from '.';

interface KanbanViewProps {
  children?: never;
  projects: FullProject[];
  filters: Filters;
}

type KanbanTask = FullProject['tasks'][number] & { projectName: string };
type FilterKey = keyof ProjectFilter;

const CONTAINER_ID = 'kanban_wrapper_projects_home';
const AVG_CARD_HEIGHT = 125;

const filterResolvers: {
  [key in FilterKey]?: (task: KanbanTask, filters: string[]) => boolean;
} = {
  taskOwners: ({ owner }, filters) => filters.includes(owner.id),

  tasks: ({ baseName }, filters) => filters.includes(baseName),
};

export function KanbanView({ projects, filters }: KanbanViewProps): JSX.Element {
  const { t } = useLang();

  const { state: hiddenControl, dispatch } = useFormControl<Record<string, boolean>>({});

  const tasksByCode = useMemo(() => {
    const tasks = projects
      .map(({ tasks, name }) => tasks.map((task) => ({ ...task, projectName: name })))
      .flat();

    return tasks.reduce(
      (acc, task) => {
        const shouldAdd = Object.entries(filters).every(
          ([filter, values]) =>
            filterResolvers[filter as FilterKey]?.(task, [values].flat()) ?? true,
        );

        if (!shouldAdd) return acc;

        return {
          ...acc,
          [task.status]: [...(acc[task.status] || []), task],
        };
      },
      {} as Record<string, KanbanTask[]>,
    );
  }, [projects, filters]);

  const statusRenderer = useMemo(() => {
    return Object.values(taskStatusesConfig)
      .sort((a, b) => a.order - b.order)
      .filter(({ onlyShowOnKanbanIfPresent, code }) =>
        onlyShowOnKanbanIfPresent ? tasksByCode[code]?.length : true,
      )
      .sort(
        (a, b) => Number(hiddenControl[b.code] ?? false) - Number(hiddenControl[a.code] ?? false),
      );
  }, [tasksByCode, hiddenControl]);

  const shownStatusList = statusRenderer.map(({ code }) => !hiddenControl[code]);

  const canHide = shownStatusList.filter(Boolean).length > 1;

  const scrollDistance = useContainerScroll(CONTAINER_ID);

  const lastCardToRender = useMemo(() => {
    const MIN_AMOUNT = 8;

    const extraRendersBasedOnScroll = Math.round(scrollDistance / AVG_CARD_HEIGHT);

    return MIN_AMOUNT + extraRendersBasedOnScroll;
  }, [scrollDistance]);

  const maxNumberOfCards = useMemo(() => {
    return Math.max(...Object.values(tasksByCode).map((list) => list.length));
  }, [tasksByCode]);

  return (
    /*
      this is arbitrary related to current top position of our container
      should be replaced by a logic exactly equal the Wrapper.tsx on our project profile in the future
    */
    <div id={CONTAINER_ID} className="scrollbar h-[calc(100vh-225px)] overflow-auto pr-2">
      <div
        className="grid items-start gap-2"
        style={{
          minHeight: AVG_CARD_HEIGHT * maxNumberOfCards,
          gridTemplateColumns: shownStatusList
            .map((show) => (show ? 'minmax(265px, 1fr)' : '40px'))
            .join(' '),
        }}
      >
        {statusRenderer.map(({ getLabel, color, code }) => (
          <Column key={`${code}-${getLabel(t)}`} className="h-full rounded-lg bg-muted p-2">
            <div className="sticky top-0 z-10 flex items-center justify-center bg-muted">
              <Button
                variant="ghost"
                className="w-full text-foreground"
                tooltipContent={hiddenControl[code] && getLabel(t)}
                onClick={() => dispatch({ [code]: canHide ? !hiddenControl[code] : false })}
              >
                <Dot color={color} />

                {!hiddenControl[code] && <span>{getLabel(t)}</span>}
              </Button>
            </div>

            {hiddenControl[code] ? (
              <span className="text-center">{tasksByCode[code]?.length || ''}</span>
            ) : (
              <>
                {tasksByCode[code]
                  ?.slice(0, lastCardToRender)
                  .map(
                    ({ name, tags, expectedDate, id, owner, projectId, projectName, subOwner }) => (
                      <Link
                        to="projectProfile"
                        params={{ projectId, taskId: id }}
                        key={`kanban-${id}`}
                        className="rounded-lg bg-background p-2 text-center text-sm font-medium transition-colors hover:bg-hover"
                      >
                        <Column alignItems="center" gap="2">
                          <strong>{projectName.replace(/_/g, ' ')}</strong>

                          <span>{name}</span>

                          {!!tags.length && (
                            <Row gap="2" justifyContent="center" flexWrap="wrap">
                              {tags.map(({ tag }) => (
                                <Tag key={`${id}-${tag.id}`} {...tag} size="medium" />
                              ))}
                            </Row>
                          )}

                          <Row gap="2" justifyContent="space-between" fullWidth>
                            <span className="text-xs">
                              {expectedDate ? formatDate(expectedDate) : t('no-date-given')}
                            </span>

                            <UserAvatarGroup
                              size={24}
                              users={[owner, subOwner]}
                              avatarClassName="text-xs"
                            />
                          </Row>
                        </Column>
                      </Link>
                    ),
                  )}
              </>
            )}
          </Column>
        ))}
      </div>
    </div>
  );
}
